import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { PhoneIcon } from "@heroicons/react/24/solid";
import { Badge, Button, Chip, List, ListItem, Popover, PopoverContent, PopoverHandler, Tooltip } from "@material-tailwind/react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Order } from "../../types";
import Dropdown from "../components/Dropdown";
import OrderStatusChip from "../components/OrderStatusChip";
import OrderTimeline from "../components/OrderTimeline";
import TilesMap from "../components/TilesMap";
import { formatDateShort, formatDateTimeShort, formatFirstLetterUpperCase, renderLocation } from "../lib/formatters";
import { formatMoney } from "../lib/money";
import phoneFormatter from "../lib/phone";
import {
	SocketRPC,
	joinOrderTrackingRoom,
	leaveOrderTrackingRoom,
	socketEmit,
} from "../lib/socket";
import useUser from "../store/user";
import SubmitOrderDisputeModal from "./Modals/SubmitOrderDispute";
import RefundChip from "./RefundChip";

const getGoogleMapsLinkFromObject = (object) => {
	const { location = {} } = object;
	const { coordinates } = location;

	if (!coordinates) {
		return "";
	}

	return `http://www.google.com/maps/place/${coordinates.latitude},${coordinates.longitude}`;
};

interface Props {
	order: Order | null;
	mode: 'active-order' | 'historical-order',
	hasNewMessage: boolean,
}

export default function OrderDetails({ order, mode = 'active-order', hasNewMessage }: Props) {
	const routerParams = useParams();
	const { user } = useUser();
	const [latestRefundDispute, setLatestRefundDispute] = useState<any>(null);

	const canChatSupport = user?.permissionKeys?.includes('SUPER_ADMIN_CAN_CHAT_SUPPORT');
	const canEditOrder = user?.permissionKeys?.includes('SUPER_ADMIN_CAN_EDIT_ORDER');
	const canRequestRefunds = user?.permissionKeys?.includes('SUPER_ADMIN_CAN_REQUEST_REFUNDS');

	const centerPanelRef = useRef<HTMLDivElement>(null);

	const [signaturePhotoURL, setSignaturePhotoURL] = useState<string>("");
	const [deliveryVerificationPhotoURLs, setDeliveryVerificationPhotoURLs] =
		useState<string[]>([]);

	const navigate = useNavigate();

	const [submitDisputeModalOpen, setSubmitDisputeModalOpen] = useState(false);

	const toggleOpen = () => {
        setSubmitDisputeModalOpen(!submitDisputeModalOpen)
    };

	useEffect(() => {
		const { orderKey = routerParams.orderKey! } = order || {};
		joinOrderTrackingRoom({orderKey});

		return () => {
			leaveOrderTrackingRoom(orderKey);
		};
	}, [order?.orderKey]);

	const refreshLatestRefundDispute = async () => {
		if (!order) {
			return;
		}
		const { orderKey } = order;

		socketEmit(SocketRPC.GetLatestRefund, {orderKey})
			.then((res) => {
				if (!res.success) {
					throw new Error('Could not load latest refund status')
				}
				setLatestRefundDispute(res.dispute);
			})
			.catch((err) => {
				console.error('Could not load latest refund status', err);
			});
	};

	useEffect(() => {
		if (!order) {
			return;
		}
		refreshLatestRefundDispute();
	}, [order])

	useEffect(() => {
		if (!order) {
			return;
		}

		if (order.deliveries && order.deliveries.length > 0) {
			order.deliveries.forEach((d) => {
				if (d.deliveryKey && d.hasDeliveryPhoto) {
					socketEmit(SocketRPC.GetS3URL, {
						folder: "delivery-verification-photos",
						contentType: "image/jpg",
						key: `${d.deliveryKey}.jpg`,
					})
						.then((res) =>
							setDeliveryVerificationPhotoURLs([
								...deliveryVerificationPhotoURLs,
								res.url,
							])
						)
						.catch((err) => console.log(`Could not fetch delivery photo`, err));
				}
			});
		}
	}, [order]);

	useEffect(() => {
		if (!order || !order.signatureRequired) {
			return;
		}

		if (order.externalId && order.producer.producerLocationKey) {
			socketEmit(SocketRPC.GetS3URL, {
				folder: `signatures/${order.producer.producerLocationKey}`,
				contentType: "image/png",
				key: `${order.externalId}.png`,
			})
				.then((res) => setSignaturePhotoURL(res.url))
				.catch((err) => console.log(`Could not fetch signature photo`));
		}
	}, [order]);

	const proofOfDeliveryLinks = useMemo(() => {
		const links: Array<{ label: string; url: string }> = [];

		if (signaturePhotoURL) {
			links.push({ label: "Signature", url: signaturePhotoURL });
		}

		(deliveryVerificationPhotoURLs || []).forEach((url, i) => {
			links.push({ label: `Delivery ${i + 1}`, url });
		});

		return links;
	}, [deliveryVerificationPhotoURLs, signaturePhotoURL]);

	if (!order) {
		return <p>Loading order...</p>;
	}

	return (
		<div className="flex flex-col h-full">
			<div className="flex items-start justify-between gap-x-8 p-6 bg-white border-gray-300">
				<div className="flex flex-col gap-y-6">
					<div>
						<div className="text-sm mb-1 text-gray-500">
							External ID:&nbsp;
							<div className="mb-1 text-base font-semibold leading-6 text-gray-900 md:inline">
								{order.externalId}
							</div>
						</div>
						<div className="text-xs text-gray-500">
							Order Key:&nbsp;
							<p className="md:inline break-all">{order.orderKey}</p>
						</div>
					</div>
					<div className="flex flex-col items-start gap-2 md:flex-row">
						{order.isRedelivery && (
							<Chip
								variant="ghost"
								size="lg"
								value="Redelivery"
								color="amber"
							/>
						)}
						<OrderStatusChip
							orderStatus={order.status?.order}
							size="lg"
						/>
					</div>
				</div>
				<div className="flex flex-col gap-y-6">
					<div className='flex flex-col items-center gap-3 md:flex-row'>
						{proofOfDeliveryLinks.length <= 1 ? (<div>
							<Button
								type="button"
								variant="outlined"
								disabled={!proofOfDeliveryLinks.length}
								onClick={() => window.open(proofOfDeliveryLinks[0].url, '_blank')}

							>Proof of Delivery</Button>
						</div>) : null}

						{proofOfDeliveryLinks.length > 1 ? (<div>
							<Dropdown
								label="Proof of Delivery"
								items={proofOfDeliveryLinks.map(link => ({ label: link.label, onClick: () => window.open(link.url, '_blank') }))}
							/>
						</div>) : null}
						{canEditOrder && mode === 'active-order' && <Button
							type="button"
							variant='outlined'
							onClick={() =>
								navigate(
									`/active-orders/${order.orderKey}/edit`,
								)
							}
						>Edit Order</Button>}
						<Badge content="1" invisible={!hasNewMessage}>
						<Popover placement="bottom">
						<PopoverHandler>
						<Button
							type="button"
							className="bg-blue-600 hover:bg-blue-400 text-white"
						>Get Support</Button>
						</PopoverHandler>
						<PopoverContent>
							<List className="p-0">
								<ListItem
									onClick={() =>
										navigate(
											`/active-orders/${order.orderKey}/support`,
										)
								}>
									Chat with Support
								</ListItem>
								{canRequestRefunds && (!latestRefundDispute || latestRefundDispute.disputeStatus === 'DENIED') && <ListItem
									onClick={() => {
										toggleOpen();
									}}>
								{`${latestRefundDispute ? 'Appeal Refund' : 'Request a Refund'}`}
								</ListItem>}
							</List>
						</PopoverContent>
						</Popover>
						</Badge>

						{/* <Button
									type="button"
									onClick={() => navigator.clipboard.writeText(order.externalId)}
									label="Copy External ID"
								/> */}
					</div>
					<div className="flex flex-col items-start gap-2 md:flex-row justify-end">
						{mode === 'historical-order' && <Button
							variant="gradient"
							size="sm"
							color="indigo"
							disabled={order.invoice?.invoiceId === null}
							onClick={() => {
								window.open(`${order.invoice?.invoiceURL}`)
							}}
						>{
							order.invoice?.invoiceId ? 
							`Invoiced on ${formatDateShort(order.invoice.invoiceDate)}` : 'Invoice Pending'
						}</Button>}
						{latestRefundDispute ? (
							latestRefundDispute.disputeStatus === 'APPROVED' ? 
							 	<RefundChip
									value="Refunded"
									color="green"
									tooltipHeader={`Refunded for ${formatMoney(latestRefundDispute?.finalRefundAmount)}`}
									tooltipContent={`${formatFirstLetterUpperCase(latestRefundDispute.disputeReason)}: ${latestRefundDispute?.reviewDescription}`}
								/>
							: latestRefundDispute.disputeStatus === 'DENIED' ?
								<RefundChip
									value="Refund Denied"
									color="red"
									variant="ghost"
									tooltipContent={`${formatFirstLetterUpperCase(latestRefundDispute.disputeReason)}: ${latestRefundDispute?.reviewDescription}`}
								/>
							: 
							<RefundChip
								value={latestRefundDispute.disputeStatus}
								variant="ghost"
								color="orange"
								tooltipHeader={`Refund of ${formatMoney(latestRefundDispute.requestedRefundAmount)} requested`}
								tooltipContent={`${formatFirstLetterUpperCase(latestRefundDispute.disputeReason)}: ${latestRefundDispute.disputeDescription}`}
							/>
						) : order.refund?.isRefunded && <RefundChip
							value="Refunded"
							color="green"
							tooltipHeader={`Refunded for ${formatMoney(order.refund?.amount)}`}
							tooltipContent={order.refund.description}
							/>
						}
					</div>
				</div>
			</div>
			<div className="grow relative mx-4 md:mx-0">
				<div className="w-full aspect-square mb-4 md:mb-0 md:h-full md:aspect-auto relative">
					<TilesMap
						orderKey={order.orderKey}
						centerPanelRef={centerPanelRef}
						order={order}
					/>
				</div>
				<div className="md:w-3/12 overflow-y-auto md:absolute top-5 left-5 bottom-5 z-10">
					{/* Producer/Consumer info */}
					<div className="w-full border border-gray-300 py-4 px-4 shadow-sm mb-4 rounded-xl bg-white">
						{order.time.isFutureOrder && (
							<div className="rounded-md p-2 mb-4 text-sm bg-cyan-300 text-white border">
								Future order due by{" "}
								{order.quote?.constraints?.before ? formatDateTimeShort(order.quote.constraints.before) : formatDateTimeShort(order.time.lateDelivery)}.
							</div>
						)}

						<h2 className="text-base font-semibold leading-6 text-gray-900">
							Consumer:
						</h2>
						<div className="mt-6 grid grid-cols-2 text-sm leading-6 border-b border-gray-300">
							<div className="mb-4">
								<p className="text-gray-500">Name</p>
								<p className="text-gray-700">
									{order.consumer?.name ?? 'Unknown'}
								</p>
							</div>

							<div className="mb-4">
								<p className="text-gray-500">Phone</p>
								<p className="text-gray-700">
									{phoneFormatter(order.consumer?.phone)}
								</p>
							</div>

							<div className="mb-4">
								<p className="text-gray-500">Address</p>
								<a
									target="_blank"
									rel="noreferrer"
									className="text-gray-700 underline hover:text-gray-500"
									href={getGoogleMapsLinkFromObject(
										order.consumer
									)}
								>
									{renderLocation(order.consumer)}
								</a>
							</div>

							<div className="mb-4 w-fit">
								<CallLogsBadge order={order} target="consumer" />
							</div>
							{order.consumer?.specialInstructions && <div className="col-span-2 mb-4">
								<span className="text-gray-500">Special Instructions: </span>
								<span className="text-gray-700 italic">{order.consumer?.specialInstructions}</span>
							</div>}
						</div>

						<h2 className="mt-4 text-base font-semibold leading-6 text-gray-900">
							Producer:
						</h2>
						<div className="grid grid-cols-2 text-sm leading-6">
							<div className="mb-4">
								<p className="text-gray-500">Name</p>
								<p className="text-gray-700">
									{order.producer?.name ?? 'Unknown'}
								</p>
							</div>

							<div className="mb-4">
								<p className="text-gray-500">Phone</p>
								<p className="text-gray-700">
									{phoneFormatter(order.producer?.phone)}
								</p>
							</div>

							<div className="mb-4">
								<p className="text-gray-500">Address</p>
								<a
									target="_blank"
									rel="noreferrer"
									className="text-gray-700 underline hover:text-gray-500"
									href={getGoogleMapsLinkFromObject(
										order.producer
									)}
								>
									{renderLocation(order.producer)}
								</a>
							</div>

							<div className="mb-4 w-fit">
								<CallLogsBadge order={order} target="producer" />
							</div>

							{order.producer?.specialInstructions && <div className="col-span-2 mb-4">
								<span className="text-gray-500">Special Instructions: </span>
								<span className="text-gray-700 italic">{order.producer?.specialInstructions}</span>
							</div>}
						</div>
					</div>

					<OrderPricesBox order={order} mode={mode} />
				</div>
				<div
					className="md:w-4/12 md:mx-auto"
					ref={centerPanelRef}
				/>
				<div className="md:w-3/12 overflow-y-auto md:absolute top-5 right-5 bottom-5 z-10">
					{/* <DeliveriesTable order={order} /> */}
					<OrderTimeline order={order} />
				</div>
			</div>
			<SubmitOrderDisputeModal
				order={order}
				submitDisputeModalOpen={submitDisputeModalOpen}
				toggleOpen={toggleOpen}
				onSubmitCompleted={refreshLatestRefundDispute}
			/>
		</div>
	);
}

const OrderPricesBox = ({ order, mode }: { order: Order, mode: string }) => {
	return (
		<div className="border border-gray-300 py-4 px-4 shadow-sm w-full mb-4 rounded-xl bg-white">
			{mode === 'active-order' && <div
				className={classNames(
					"rounded-md p-2 text-sm",
					order.isConfirmed
						? "bg-green-300 text-white border"
						: "bg-red-300 text-white"
				)}
			>
				{order.isConfirmed ? "Confirmed" : "Not confirmed"}
			</div>}

			<div className="flex items-center mt-4">
				<CalendarDaysIcon className="h-6 w-6 text-gray-400" />

				<span className="text-sm ml-3 text-gray-500">
					Created at{" "}
					{DateTime.fromISO(order.createdAt).toLocaleString(
						DateTime.DATETIME_MED
					)}
					.
				</span>
			</div>

			{order.specialInstructions && <div className="mt-4 px-1">
				<span className="text-gray-500 text-sm">Special Instructions: </span>
				<span className="text-gray-700 text-sm italic">{order.specialInstructions}</span>
			</div>}

			<div className="grid grid-cols-2 grid-rows-2">
				<div className="flex-auto p-5">
					<div className="text-sm font-semibold leading-6 text-gray-600">
						Subtotal
					</div>
					<div className="mt-1 text-base font-semibold leading-6 text-gray-900">
						{formatMoney(order.price.subTotal)}
					</div>
				</div>

				<div className="flex-auto p-5">
					<div className="text-sm font-semibold leading-6 text-gray-600">
						Tax
					</div>
					<div className="mt-1 text-base font-semibold leading-6 text-gray-900">
						{formatMoney(order.price.tax)}
					</div>
				</div>

				<div className="flex-auto p-5">
					<div className="text-sm font-semibold leading-6 text-gray-600">
						Tip
					</div>
					<div className="mt-1 text-base font-semibold leading-6 text-gray-900">
						{formatMoney(order.price.tip)}
					</div>
				</div>

				<div className="flex-auto p-5">
					<div className="text-sm font-semibold leading-6 text-gray-600">
						Total
					</div>
					<div className="mt-1 text-base font-semibold leading-6 text-gray-900">
						{formatMoney(
							order.price.subTotal + order.price.tax + order.price.tip
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

const CallLogsBadge = ({ order, target }) => {
	const { callLogs = [] } = order;
	const callsToTarget = callLogs.filter(l => l.target === target);

	if (!callsToTarget || callsToTarget.length === 0) {
		return null;
	}

	return (
		// <div className='w-full'>
		<Tooltip
			content={
				<div className="flex flex-col max-w-xs">
					<ul>
						<li>Called by rider at:</li>
						{callsToTarget.map((call) => <li>{formatDateTimeShort(call.timestamp)}</li>)}
					</ul>
				</div>
			}
			placement="right"
		>
			<div className='flex flex-row text-sm justify-start gap-1 font-semibold leading-6 text-gray-700 bg-orange-100 rounded-lg pl-2 pr-3 items-center'>
				<PhoneIcon className={`h-4 w-4 text-gray-800`} />
				Called {callsToTarget.length}x
			</div>
		</Tooltip>
	)
};
